<template>
  <Button
    v-if="buttonIsVisible"
    v-tooltip.top="tooltipText"
    type="button"
    text
    class="p-inputgroup-addon placeholder-source-btn"
    :class="`placeholder-source-${placeholderSource}`"
    :disabled="disabled"
    tabindex="-1"
    @click="handleClick"
  >
    <i :class="iconClass" />
  </Button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';

import type { InvoiceItemSuggestionSource } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';

interface Props {
  /** Source of the placeholder (determines icon and color) */
  placeholderSource?: InvoiceItemSuggestionSource;
  /** The placeholder value (already formatted for display) */
  placeholder?: string | null;
  /** Current value in the form field */
  currentValue: any;
  /** Whether the field is disabled */
  disabled?: boolean;
  /**
   * Override visibility control. If provided, uses this instead of auto-detection.
   * Useful when parent component has custom comparison logic (e.g., for dates)
   */
  isVisible?: boolean;
  /**
   * Custom comparison function for complex types (e.g., dates).
   * Only used if isVisible is not explicitly provided.
   */
  canonicalValue?: (val: any) => any;
}

interface Emits {
  (e: 'accept'): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholderSource: undefined,
  placeholder: undefined,
  disabled: false,
  isVisible: undefined,
  canonicalValue: undefined,
});

const emit = defineEmits<Emits>();
const { t } = useI18n();

/**
 * Determines if button should be visible
 * If isVisible is explicitly provided, use that.
 * Otherwise, auto-detect based on placeholder and current value.
 */
const buttonIsVisible = computed(() => {
  // If parent explicitly set isVisible, use that
  if (props.isVisible !== undefined) {
    return props.isVisible;
  }

  // Auto-detect: show only if placeholderSource, placeholder exist, field is enabled, and values differ
  if (!props.placeholderSource || !props.placeholder || props.disabled) {
    return false;
  }

  // Use canonical form if provided, otherwise direct comparison
  const canonical = props.canonicalValue || ((val: any) => val);
  const currentCanonical = canonical(props.currentValue);
  const placeholderCanonical = canonical(props.placeholder);

  return currentCanonical !== placeholderCanonical;
});

/**
 * Determines the icon class based on placeholder source
 */
const iconClass = computed(() => {
  switch (props.placeholderSource) {
    case 'ai':
      return 'pi pi-sparkles'; // AI: sparkles icon
    case 'latest':
      return 'pi pi-receipt'; // Latest/Previous: receipt icon
    case 'other':
      return 'pi pi-circle-fill'; // Other: circle icon
    default:
      return '';
  }
});

/**
 * Tooltip text showing the proposed value
 */
const tooltipText = computed(() => {
  if (!props.placeholder) {
    return undefined;
  }

  return t('labels.proposedValue', { value: props.placeholder });
});

function handleClick() {
  emit('accept');
}
</script>

<style scoped>
/* Placeholder source icon styling */
/* Uses CSS variables from src/assets/colors.css */
/* Colors automatically adapt to Light/Dark mode */

.placeholder-source-btn {
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.5rem;
  min-width: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  border-right: none;
}

/* AI suggestions: Purple palette */
.placeholder-source-ai {
  color: var(--placeholder-ai-color);
  background-color: var(--placeholder-ai-background);
  border-color: var(--placeholder-ai-border);
}

.placeholder-source-ai:hover {
  color: var(--placeholder-ai-hover-color);
  background-color: var(--placeholder-ai-hover-background);
}

/* Latest/Previous data: Blue palette */
.placeholder-source-latest {
  color: var(--placeholder-latest-color);
  background-color: var(--placeholder-latest-background);
  border-color: var(--placeholder-latest-border);
}

.placeholder-source-latest:hover {
  color: var(--placeholder-latest-hover-color);
  background-color: var(--placeholder-latest-hover-background);
}

/* Other suggestions: Gray palette */
.placeholder-source-other {
  color: var(--placeholder-other-color);
  background-color: var(--placeholder-other-background);
  border-color: var(--placeholder-other-border);
}

.placeholder-source-other:hover {
  color: var(--placeholder-other-hover-color);
  background-color: var(--placeholder-other-hover-background);
}
</style>
